import {
  Component,
  Input,
  Output,
  EventEmitter,
  ViewChild,
  OnChanges,
  SimpleChanges,
} from '@angular/core';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import { QuestionType } from 'src/app/app.types';
import { QuestionsOverview } from '../question-overview-list/question-overview-list.component';
import {
  AiQuestionEvaluation,
  AiQuestionEvaluationResult,
  ExamResultModel,
} from 'src/app/api/models';
import { DonutSection } from 'src/app/widget/donut-chart.component';
import { Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { ImportAiExplanationComponent } from '../../dialogs/import-ai-explanation/import-ai-explanation.component';
import { NgScrollbar } from 'ngx-scrollbar';
import { ContentSpinnerComponent } from '../../ui/content-spinner.component';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { NgClass } from '@angular/common';
import { ImportExamNavigationComponent } from '../../import-exam-navigation.component';
import {
  MatButtonToggleGroup,
  MatButtonToggle,
} from '@angular/material/button-toggle';
import { ImageDisplayComponent } from '../../../../widget/image-display/image-display.component';
import { MarkdownKatexComponent } from '../../../../widget/markdown-katex/markdown-katex.component';
import { WordCloudVisualizationComponent } from '../../word-cloud-visualization/word-cloud-visualization.component';
import { DonutChartComponent } from '../../../../widget/donut-chart.component';
import { MatCheckbox } from '@angular/material/checkbox';
import { CustomChipComponent } from '../../../../shared/components/custom-chip/custom-chip.component';
import { MatFormField, MatLabel } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { FormsModule } from '@angular/forms';
import { ButtonComponent } from '../../../../shared/components/button/button.component';
import { MatTooltip } from '@angular/material/tooltip';
import { MatMenuTrigger, MatMenu, MatMenuItem } from '@angular/material/menu';
import { AiService, QuestionsService } from 'src/app/api/services';
import { SampleAnswerComponent } from '../sample-answer/sample-answer.component';
import { forkJoin, Subscription } from 'rxjs';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatExpansionModule } from '@angular/material/expansion';
import {
  generateScoreSteps,
  validateQuestions,
  validateSchema,
} from 'src/app/utility/app.utils';
import { isNil } from 'lodash';
import { TrackingService } from 'src/app/utility/amplitude-tracking/tracking.service';
import { schemas } from 'src/app/payload-validators';
import { FreeTextAnswersGridComponent } from '../../free-text-answers-grid/free-text-answers-grid.component';

@Component({
  selector: 'qa-question-overview-details',
  templateUrl: './question-overview-details.component.html',
  styleUrls: ['./question-overview-details.component.scss'],
  imports: [
    ContentSpinnerComponent,
    NgScrollbar,
    MatButton,
    MatIcon,
    ImportExamNavigationComponent,
    MatButtonToggleGroup,
    MatButtonToggle,
    ImageDisplayComponent,
    MarkdownKatexComponent,
    WordCloudVisualizationComponent,
    DonutChartComponent,
    MatCheckbox,
    NgClass,
    CustomChipComponent,
    MatFormField,
    MatLabel,
    MatInput,
    FormsModule,
    ButtonComponent,
    MatIconButton,
    MatTooltip,
    MatMenuTrigger,
    MatMenu,
    MatMenuItem,
    TranslateModule,
    SampleAnswerComponent,
    MatExpansionModule,
    FreeTextAnswersGridComponent,
  ],
})
export class QuestionOverviewDetailsComponent implements OnChanges {
  @Input() loading: boolean;
  @Input() getQuestionResult;
  @Input() hideUnAnswered = false;
  @Input() currentItemIndex;
  @Input() question: QuestionsOverview;
  @Input() allQuestions: QuestionsOverview[];
  @Input() answerSections: DonutSection[];
  @Output() goBack = new EventEmitter<void>();
  @Output() selectScore = new EventEmitter<{
    submission: ExamResultModel;
    score: number;
  }>();
  @Output() setFeedback = new EventEmitter<{ submission: ExamResultModel }>();
  explanationOpen = false;
  questionType = QuestionType;
  selectedVisualization = 'list';
  @ViewChild(NgScrollbar) scrollbarRef: NgScrollbar;
  submittingAns = false;
  expanded = false;
  gradingAllInProgress = false;
  subscription: Subscription;

  constructor(
    private matSnackBar: MatSnackBar,
    private translator: TranslateService,
    private questionsService: QuestionsService,
    private matDialog: MatDialog,
    private aiService: AiService,
    private router: Router,
    private trackingService: TrackingService
  ) {}

  get labelAnswers() {
    return this.translator.instant('exam.result.ungradedAnswers', {
      count: this.question.openQuestionAnsweredCount,
    });
  }

  get isExam() {
    return this.router.url.indexOf('cms/exams') !== -1;
  }

  get sampleAnswer(): string {
    if (this.question?.is_poll_question) {
      return '';
    }
    return this.question.answers?.length
      ? this.question.answers[0].text
      : this.translator.instant('exam.result.noSampleAnswer');
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes?.loading) {
      this.handleScroll(500);
    }
  }

  translateTrueFalse(text): string {
    if (text.toLowerCase() === 'true' || text === true) {
      return this.translator.instant('question.form.correct');
    } else if (text.toLowerCase() === 'false' || text === false) {
      return this.translator.instant('question.form.inCorrect');
    } else {
      return text;
    }
  }

  getAnswerBarWidth(amount: number): string {
    const quotient = amount / this.question.total_result || 0;
    return `${Math.round(quotient * 100)}%`;
  }

  getCorrectRatio(stat: QuestionsOverview) {
    return stat && stat.total_result > 0
      ? `${((stat.num_correct_answers * 100) / stat.total_result).toFixed(2)}%`
      : '0%';
  }

  getRightResult(question: QuestionsOverview) {
    const total =
      question.num_correct_answers +
      question.num_incorrect_answer +
      question.num_unanswered;
    const quotient = question.num_correct_answers / total || 0;
    return `${Math.round(quotient * 100)}%`;
  }

  getWrongResult(question: QuestionsOverview) {
    const total =
      question.num_correct_answers +
      question.num_incorrect_answer +
      question.num_unanswered;
    const quotient = question.num_incorrect_answer / total || 0;
    return `${Math.round(quotient * 100)}%`;
  }

  onNavigate(questionIndex: number) {
    if (questionIndex < this.allQuestions?.length) {
      this.getQuestionResult({
        result: this.allQuestions[questionIndex],
        index: questionIndex,
      });
    }
  }

  scoreSteps(maxPoints: number) {
    return generateScoreSteps(maxPoints);
  }

  goBackToResult() {
    this.goBack.emit();
  }

  selectScoreQuestion(score: { submission: ExamResultModel; score: number }) {
    if (score.submission.score === score.score) {
      return;
    }
    if (
      score.score === null &&
      (score.submission.score || score.submission.score === 0)
    ) {
      this.question.openQuestionAnsweredCount++;
    }
    if (
      (score.score || score.score === 0) &&
      !score.submission.score &&
      score.submission.score !== 0
    ) {
      this.question.openQuestionAnsweredCount--;
    }
    this.selectScore.emit(score);
  }

  showFeedback(submission) {
    submission.addReply = !submission.addReply;
    this.handleScroll();
  }

  mapToStrings(): string[] {
    const answers = this.question.answers;
    return answers?.length
      ? answers.reduce(
          (p, i) => [...p, ...new Array(i.num_selected).fill(i.text)],
          []
        )
      : [];
  }

  generateExplanation(submission) {
    const answer = this.question.answers?.length
      ? this.question.answers[0]
      : null;
    const data: AiQuestionEvaluation = {
      answer: submission?.selected_answers?.length
        ? submission?.selected_answers[0].text
        : null,
      expected_answer: answer ? answer.text : null,
      question: this.question.text,
      max_score: this.question.max_score,
    };
    const sentryInfo = {
      title: `Error while evaluate question result with AI`,
      file: 'question-overview-details.component.ts',
      api: 'evaluateQuestion',
    };
    const { isInvalidSchema } = validateSchema(
      schemas.AIQuestionEvaluation,
      data,
      this.matSnackBar,
      sentryInfo
    );
    if (isInvalidSchema) {
      return null;
    }
    this.matDialog
      .open<unknown, any>(ImportAiExplanationComponent, {
        data,
        width: '600px',
      })
      .afterClosed()
      .subscribe((response) => {
        if (response) {
          submission.addReply = true;
          submission.free_text_feedback = response.feedback;
          this.selectScoreQuestion({ submission, score: response.score });
          this.handleScroll();
        }
      });
  }

  handleSampleAnswerChange($event) {
    if (this.submittingAns) {
      return;
    }
    this.submittingAns = true;
    if (this.question && this.question.answers?.length) {
      this.question.answers[0].text = $event;
    } else {
      this.question.answers = [
        {
          text: $event,
          num_selected: null,
          question_id: this.question.id,
        },
      ];
    }
    const sentryInfo = {
      title: `Error while update question`,
      file: 'question-overview-details.component.ts',
      api: 'updateQuestion',
    };
    const { isValid } = validateQuestions(
      sentryInfo,
      [this.question],
      this.translator,
      this.matSnackBar
    );
    if (!isValid) {
      this.submittingAns = false;
      return;
    }
    this.questionsService
      .updateQuestion({ id: this.question.id, body: this.question })
      .subscribe(() => {
        this.submittingAns = false;
      });
  }

  gradeAllWithAi(submissions, ungradedOnly: boolean) {
    this.gradingAllInProgress = true;
    this.handleScroll();
    const requests$ = [];
    const answer = this.question.answers?.length
      ? this.question.answers[0]
      : null;
    submissions.forEach((submission) => {
      // ungradedOnly flag is for including ungraded free text answers only
      // This condition is for checking if we want to grade all free text answers or only ungraded answers
      // isNil(submission.score) is identifies ungraded question answer if score is empty/null/undefined
      if (!ungradedOnly || (ungradedOnly && isNil(submission.score))) {
        const aiRequest: AiQuestionEvaluation = {
          expected_answer: answer ? answer.text : null,
          question: this.question.text,
          max_score: this.question.max_score,
          answer: submission?.selected_answers?.length
            ? submission?.selected_answers[0].text
            : null,
        };
        requests$.push({
          id: submission.id,
          evaluation: aiRequest,
          request: this.aiService.evaluateQuestion({ body: aiRequest }),
        });
      }
    });
    if (!requests$.length) {
      this.gradingAllInProgress = false;
      return;
    }
    const evaluations = requests$.map((req) => req.evaluation);
    const sentryInfo = {
      title: `Error while evaluate questions result with AI`,
      file: 'question-overview-details.component.ts',
      api: 'evaluateQuestion',
    };
    const { isInvalidSchema } = validateSchema(
      schemas.AIQuestionEvaluation.array(),
      evaluations,
      this.matSnackBar,
      sentryInfo
    );
    if (isInvalidSchema) {
      this.gradingAllInProgress = false;
      return null;
    }
    this.subscription = forkJoin(requests$.map((r) => r.request)).subscribe({
      next: (response: AiQuestionEvaluationResult[]) => {
        this.trackingService.trackAIEvaluationEvent(requests$.length);
        this.gradingAllInProgress = false;
        if (response && response?.length) {
          submissions.forEach((submission) => {
            const uIdx = requests$.findIndex((r) => {
              return r.id === submission.id;
            });
            if (uIdx !== -1) {
              submission.addReply = true;
              submission.free_text_feedback = response[uIdx].feedback;
              this.selectScoreQuestion({
                submission,
                score: response[uIdx].evaluation_result,
              });
            }
          });
          this.handleScroll();
        }
      },
      error: () => {
        this.gradingAllInProgress = false;
        this.matSnackBar.open(
          this.translator.instant('AIQuestionImports.errorToast')
        );
      },
    });
  }

  cancelRequest() {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
    this.gradingAllInProgress = false;
  }

  handleScroll(duration = 10) {
    setTimeout(() => {
      if (this.scrollbarRef) {
        this.scrollbarRef.update();
      }
    }, duration);
  }
}
